import { useState } from 'react'
import { useFunnelVelocity } from '@/hooks/useOpportunities'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { formatCurrency } from '@/lib/utils'
import { GitBranch, Loader2, Clock, TrendingDown, Info } from 'lucide-react'

type Period = 30 | 90 | 180 | 365

const PERIOD_LABELS: Record<number, string> = {
  30: '30d',
  90: '90d',
  180: '6m',
  365: '12m',
}

const FUNNEL_COLORS = [
  'bg-blue-500',
  'bg-indigo-500',
  'bg-purple-500',
  'bg-pink-500',
  'bg-emerald-500',
]

export function FunnelVelocitySection() {
  const [period, setPeriod] = useState<Period>(180)
  const { data, isLoading, error } = useFunnelVelocity(period)

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5" />
            Funil de conversão & velocidade
            <div className="relative group">
              <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              <div className="absolute left-6 top-0 hidden group-hover:block bg-popover text-popover-foreground border rounded-md p-3 text-xs w-80 shadow-lg z-50 font-normal">
                <p className="mb-2">Mostra o caminho dos <strong>leads criados no período</strong> pelo pipeline de vendas.</p>
                <p className="mb-2"><strong>% ao lado de cada etapa:</strong> taxa de conversão da etapa anterior para esta. Ex.: 11% em &quot;Qualificado&quot; significa que 11% dos leads viraram opp qualificada.</p>
                <p className="mb-1"><strong>Cycle Time:</strong> tempo médio entre criação e ganho (só deals Won).</p>
                <p><strong>Win Rate:</strong> ganhos ÷ (ganhos + perdidos).</p>
              </div>
            </div>
          </CardTitle>
          <div className="flex items-center gap-1 bg-muted rounded-md p-1">
            {([30, 90, 180, 365] as Period[]).map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-2.5 py-1 text-xs rounded transition-colors ${
                  period === p ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {PERIOD_LABELS[p]}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Carregando…
          </div>
        )}
        {error && <div className="text-sm text-destructive py-4">Erro ao carregar funil</div>}
        {data && !isLoading && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pb-3 border-b">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Cycle Time médio</div>
                <div className="text-xl font-bold flex items-baseline gap-1">
                  {data.avg_cycle_days_won != null ? data.avg_cycle_days_won : '—'}
                  {data.avg_cycle_days_won != null && <span className="text-sm font-normal text-muted-foreground">dias</span>}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Win Rate</div>
                <div className="text-xl font-bold">
                  {data.win_rate_pct != null ? `${data.win_rate_pct}%` : '—'}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Ganhas</div>
                <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{data.won_count}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Perdidas</div>
                <div className="text-xl font-bold text-red-600 dark:text-red-400">{data.lost_count}</div>
              </div>
            </div>

            <div className="space-y-2">
              {data.funnel.map((s, i) => {
                const max = Math.max(...data.funnel.map(x => x.count), 1)
                const widthPct = (s.count / max) * 100
                const isDropoff = s.conversion_from_prev_pct != null && s.conversion_from_prev_pct < 50
                return (
                  <div key={s.stage} className="flex items-center gap-3">
                    <div className="w-32 text-xs text-right text-muted-foreground truncate">
                      {s.label}
                    </div>
                    <div className="flex-1 bg-muted rounded-md h-8 relative overflow-hidden">
                      <div
                        className={`h-full ${FUNNEL_COLORS[i % FUNNEL_COLORS.length]} transition-all flex items-center px-2 text-white text-xs font-semibold`}
                        style={{ width: `${widthPct}%`, minWidth: s.count > 0 ? '2rem' : '0' }}
                      >
                        {s.count > 0 && s.count}
                      </div>
                    </div>
                    <div className="w-24 text-xs text-right text-muted-foreground">
                      {s.stage === 'lead' ? '—' : formatCurrency(s.value)}
                    </div>
                    <div className="w-16 text-xs text-right">
                      {s.conversion_from_prev_pct != null ? (
                        <span className={isDropoff ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-muted-foreground'}>
                          {isDropoff && <TrendingDown className="h-3 w-3 inline mr-0.5" />}
                          {s.conversion_from_prev_pct}%
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {data.velocity_by_owner.length > 0 && (
              <div className="pt-3 border-t">
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Cycle time por vendedor (deals ganhos)
                </div>
                <div className="space-y-1">
                  {data.velocity_by_owner.map(o => (
                    <div key={o.owner_id} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{o.owner_name}</span>
                      <span>
                        <strong>{o.avg_cycle_days ?? '—'}</strong>
                        <span className="text-muted-foreground text-xs"> dias · {o.won_count} ganho{o.won_count !== 1 ? 's' : ''}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
